import ListFilters from "../logic/ListFilters.js";
import { templateParse, templateParseToElement } from "../logic/templateParse.js";
import { ColumnData, ListFilter } from "../types/ListManagerTypes.js";
import { BaseWebComponent } from "./base-components/base-web-component.js";
import { FilterAmbitoComponent } from "./filters/filter-ambito-component.js";
import { FilterCategoriesComponent } from "./filters/filter-categories-component.js";
import { FilterErogantiComponent } from "./filters/filter-eroganti-component.js";
import { FilterSelectComponent } from "./filters/filter-select-component.js";
import { FilterSimpleComponent } from "./filters/filter-simple-component.js";
import { isNumber } from "@bonusmap/shared_ui/library";
interface FullSearchFilter {
    filterElement: FilterSelectComponent | FilterSimpleComponent | FilterAmbitoComponent | FilterCategoriesComponent | FilterErogantiComponent;
    filterContainer: HTMLElement;
    colData: ColumnData;
}
export class FullSearchComponent extends BaseWebComponent {
    static webComponentName = "full-search-component";
    protected _templateId = 'full-search-component-template';
    constructor() {
        super();
        this.setup();
    }

    private _listFilters!: ListFilters;

    get listFilters(): ListFilters {
        return this._listFilters;
    }
    set listFilters(value: ListFilters) {
        this._listFilters = value;
        const columnsData = value.columns.filter(col => {
            return col.type == "data" && (col as ColumnData).filterable;
        }) as ColumnData[];
        this.availableFields = columnsData;
    }
    private _availableFields: ColumnData[] = [];
    get availableFields(): ColumnData[] {
        return this._availableFields;
    }
    private set availableFields(value: ColumnData[]) {
        this._availableFields = value;
        this.render();
    }

    private _activeFilters: FullSearchFilter[] = [];
    get activeFilters(): FullSearchFilter[] {
        return this._activeFilters;
    }

    get renderedFields(): ColumnData[] {
        return this.availableFields.filter(col => {
            const exists = this.filterExists(col.name);
            const isSingle = col.filterSingle || false;
            return !exists || (exists && !isSingle);
        });
    }

    private notImplementedFilterElement(): HTMLElement {
        const el = document.createElement('div');
        el.className = 'alert alert-warning';
        el.textContent = 'Questo tipo di filtro non è ancora implementato.';
        return el;
    }
    private createFilterElement(colData: ColumnData): FilterSelectComponent | FilterSimpleComponent | FilterAmbitoComponent | HTMLElement {
        const filterType = colData.filterType || 'simple';
        if (filterType === "select") {
            const elFilter: FilterSelectComponent = document.createElement('form-select-filter') as FilterSelectComponent;
            elFilter.initialize(colData);
            return elFilter;
        }

        if (filterType === "simple") {
            const elFilter: FilterSimpleComponent = document.createElement('form-simple-filter') as FilterSimpleComponent;
            elFilter.initialize(colData);
            return elFilter;
        }
        if (filterType === "ambito") {
            const elFilter: FilterAmbitoComponent = document.createElement('form-ambito-filter') as FilterAmbitoComponent;
            elFilter.initialize(colData);
            return elFilter;
        }
        if (filterType === "categories") {
            const elFilter: FilterCategoriesComponent = document.createElement('form-categories-filter') as FilterCategoriesComponent;
            elFilter.initialize(colData);
            return elFilter;
        }
        if (filterType === "eroganti") {
            const elFilter: FilterErogantiComponent = document.createElement('form-eroganti-filter') as FilterErogantiComponent;
            elFilter.initialize(colData);
            return elFilter;
        }
        else {
            return this.notImplementedFilterElement();
        }
    }
    private isValidFilter(filter: ListFilter): boolean {
        switch (filter.type) {
            case 'simple':
                const simpleFilter = filter as any; // SimpleFilter type

                // NULL operators don't need search term
                if (simpleFilter.operator === 'IS NULL' || simpleFilter.operator === 'IS NOT NULL') {
                    return true;
                }

                // BETWEEN operator needs array with 2 valid values
                if (simpleFilter.operator === 'BETWEEN') {

                    return Array.isArray(simpleFilter.searchTerm) &&
                        simpleFilter.searchTerm.length === 2 &&
                        simpleFilter.searchTerm[0] !== undefined &&
                        simpleFilter.searchTerm[0] !== '' &&
                        simpleFilter.searchTerm[1] !== undefined &&
                        simpleFilter.searchTerm[1] !== '';
                }

                // Handle different value types
                if (typeof simpleFilter.searchTerm === 'string') {
                    return simpleFilter.searchTerm.trim() !== '';
                } else if (typeof simpleFilter.searchTerm === 'number') {
                    return isNumber(simpleFilter.searchTerm) && simpleFilter.searchTerm > 0;
                } else if (typeof simpleFilter.searchTerm === 'boolean') {
                    return true; // Boolean is always valid (true or false)
                } else if (simpleFilter.searchTerm === undefined) {
                    return false; // undefined means no filter (indeterminate state) - skip this filter
                } else if (simpleFilter.searchTerm === null) {
                    return false; // null is invalid
                } else {
                    return false; // Unknown type
                }
            case 'select':
                return filter.searchTerm !== undefined && filter.searchTerm !== null && filter.searchTerm !== '';
            case 'ambito':
                return filter.searchTerm !== undefined && Array.isArray(filter.searchTerm) && filter.searchTerm.length > 0;
            case 'categories':
                return filter.searchTerm !== undefined && Array.isArray(filter.searchTerm) && filter.searchTerm.length > 0;
            case 'eroganti':
                return filter.searchTerm !== undefined && Array.isArray(filter.searchTerm) && filter.searchTerm.length > 0;
            default:
                console.warn('Unknown filter type');
                return false;
        }
    }
    public collectFiltersFromComponents(): ListFilter[] {
        const filters: ListFilter[] = [];
        this._activeFilters.forEach(activeFilter => {
            const filterElement = activeFilter.filterElement;
            if (filterElement instanceof FilterSelectComponent || 
                filterElement instanceof FilterSimpleComponent || 
                filterElement instanceof FilterAmbitoComponent || 
                filterElement instanceof FilterCategoriesComponent || 
                filterElement instanceof FilterErogantiComponent) {
                const filter = filterElement.generateFilter();
                if (filter && this.isValidFilter(filter)) {
                    filters.push(filter);
                }
                
            } else {
                console.warn(`Unsupported filter type for column: ${activeFilter.colData.name}`);
            }
        });
        return filters;
    }
    private onChange() {
        const filters = this.collectFiltersFromComponents();
        this.dispatch('filters-change', { filters: filters });
        console.log('Filters changed:', filters);
        // Handle change event for the full search component
    }
    private filterExists(columnName: string): boolean {
        return this._activeFilters.some(filter => filter.colData.name === columnName);
    }
    private addFilter(colData: ColumnData): void {
        const filterElement = this.createFilterElement(colData);
        if (filterElement instanceof FilterSelectComponent || 
            filterElement instanceof FilterSimpleComponent || 
            filterElement instanceof FilterAmbitoComponent || 
            filterElement instanceof FilterCategoriesComponent || 
            filterElement instanceof FilterErogantiComponent
        ) {
            const fseId = this.createRandomId();
            const fseHTML = templateParse("full-search-element", { ...colData, id: fseId });
            const fseElement = this.createElement(fseHTML || '<div></div>');
           const fullSearchElementFilterEl = fseElement?.querySelector(`[role="full-search-element-filter"]`)
            if (fseElement) {
                this.listFiltersContainer?.appendChild(fseElement);
                fullSearchElementFilterEl?.appendChild(filterElement);
                this._activeFilters.push({
                    filterElement: filterElement,
                    filterContainer: fseElement,
                    colData: colData
                });
            }
            (filterElement as HTMLElement).addEventListener('change-value', (e: Event) => {
                const detail = (e as CustomEvent).detail;
                this.onChange();
                console.log(`Filter changed: ${detail.value}`);
            });
            (filterElement as HTMLElement).addEventListener('change-operator', (e: Event) => {
                const detail = (e as CustomEvent).detail;
                this.onChange();
                console.log(`Operator changed: ${detail.operator}`);
            });
        } else {
            console.error(`Unsupported filter type for column: ${colData.name}`);
        }
        this.updateListTablesSelect();
    }
    private selectContainer: HTMLElement | null = null;
    private listTablesSelect: HTMLSelectElement | null = null;
    private listFiltersContainer: HTMLElement | null = null;
    private fullsearchSelector: HTMLElement | null = null;
    private updateListTablesSelect(): void {
        if (this.selectContainer) {
            templateParseToElement("full-search-field-select-template", this.selectContainer, {
                fields: this.renderedFields
            })
            this.listTablesSelect = this.roleElementSelector('list-fields') as HTMLSelectElement;
        }
        this.refreshUI();
    }
    private refreshUI(): void {
        const hasFilters = this._activeFilters.length>0;
        if (this.fullsearchSelector) {
            this.fullsearchSelector.classList.toggle('has-filters', hasFilters);
        }
    }
    private setup(): void {
        this.on("change", `[role="list-fields"]`, (e: Event) => {
            const selectedValue = (e.target as HTMLSelectElement).value;
            const selectedColumn = this.availableFields.find(col => col.name === selectedValue);
            if (selectedColumn) {
                this.addFilter(selectedColumn);
                this.listTablesSelect!.value = ''; // Reset select after adding filter
            }
        });
        this.on("click", `[role="full-search-element-remove"]`, (e: Event) => {
            const filterElement = e.target as HTMLElement;
            const filterContainer = filterElement.closest('[role="full-search-element"]');
            if (filterContainer) {
                const filterIndex = this._activeFilters.findIndex(f => f.filterContainer === filterContainer);
                if (filterIndex !== -1) {
                    this.listFiltersContainer?.removeChild(filterContainer);
                    this._activeFilters.splice(filterIndex, 1);
                    this.onChange();
                    this.updateListTablesSelect();
                }
            }
        });
        this.on("click", `[role="remove-all-button"]`, () => {
            this._activeFilters.forEach(activeFilter => {
                this.listFiltersContainer?.removeChild(activeFilter.filterContainer);
            });
            this._activeFilters = [];
            this.onChange();
            this.updateListTablesSelect();
        });
    }
    protected afterRender(): void {
        this.selectContainer = this.roleElementSelector('select-container') as HTMLElement;
        this.listFiltersContainer = this.roleElementSelector('list-filters') as HTMLElement;
        this.fullsearchSelector = this.querySelector('.fullsearch-selector') as HTMLElement;
        this.updateListTablesSelect();
    }
    protected render(): void {
        // Render the component with the available fields
        templateParseToElement(this.templateId, this, {
            fields: this.renderedFields
        });
        this.afterRender();
    }
}